import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Box, Paper, Tabs, Tab, Typography, Button } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Delete as DeleteIcon } from "@mui/icons-material";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { DashboardStackCustom } from "components/StackCustom/styles";
import BackdropLoading from "components/BackdropLoading";
import { useNavigation } from "contexts/notificationsContext";
import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import { debounce } from "lodash";
import { ModalAdicionarN1 } from "./components/ModalAdicionarN1";
import { ModalAdicionarN2 } from "./components/ModalAdicionarN2";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`mobi-tabpanel-${index}`}
      aria-labelledby={`mobi-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface ILiberacaoN1 {
  id: number;
  tipoPacote: string;
  referencia: string;
  linha: string;
  data: string;
  situacao: string;
}

interface ILiberacaoN2 {
  id: number;
  projetista: string;
  linhaSistema: string;
  data: string;
  situacao: string;
}

/**
 * Tela de Liberações de Portfólios para Cliente CEM MOBI específico
 * Exibe as abas CADASTRO N1 e CADASTRO N2 com os recursos liberados
 */
export const LiberacoesMOBIDetalhe = () => {
  const { openNotification } = useNavigation();
  const navigate = useNavigate();
  const { inquilinoId } = useParams<{ inquilinoId: string }>();
  const location = useLocation();
  const nomeInquilinoFromState = (location.state as any)?.nomeInquilino;

  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [liberacoesN1, setLiberacoesN1] = useState<ILiberacaoN1[]>([]);
  const [liberacoesN2, setLiberacoesN2] = useState<ILiberacaoN2[]>([]);
  const [inquilinoNome, setInquilinoNome] = useState(nomeInquilinoFromState || "");

  // Estados dos modais
  const [modalN1Open, setModalN1Open] = useState(false);
  const [modalN2Open, setModalN2Open] = useState(false);

  // Seleção para remoção em lote
  const [selectedN1, setSelectedN1] = useState<number[]>([]);
  const [selectedN2, setSelectedN2] = useState<number[]>([]);

  // Filtros CADASTRO N1
  const [filtersN1, setFiltersN1] = useState({
    tipoPacote: "",
    referencia: "",
    linha: "",
    data: "",
    situacao: "",
  });

  // Filtros CADASTRO N2
  const [filtersN2, setFiltersN2] = useState({
    projetista: "",
    linhaSistema: "",
    data: "",
    situacao: "",
  });

  useEffect(() => {
    if (inquilinoId) {
      carregarDados();
    }
  }, [inquilinoId]);

  const carregarDados = async () => {
    setIsLoading(true);
    try {
      const [responseN1, responseN2] = await Promise.all([
        api.get(`/LiberacaoMOBI/cadastrosN1/${inquilinoId}`),
        api.get(`/LiberacaoMOBI/cadastrosN2/${inquilinoId}`)
      ]);

      setLiberacoesN1(responseN1.data || []);
      setLiberacoesN2(responseN2.data || []);
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRemoverN1 = async (id: number) => {
    if (!window.confirm("Deseja realmente remover esta liberação de Cadastro N1?")) {
      return;
    }

    setIsLoading(true);
    try {
      await api.delete(`/LiberacaoMOBI/cadastroN1/${id}`);
      openNotification({
        message: "Liberação N1 removida com sucesso",
        type: "success",
      });
      carregarDados();
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoverN2 = async (id: number) => {
    if (!window.confirm("Deseja realmente remover esta liberação de Cadastro N2?")) {
      return;
    }

    setIsLoading(true);
    try {
      await api.delete(`/LiberacaoMOBI/cadastroN2/${id}`);
      openNotification({
        message: "Liberação N2 removida com sucesso",
        type: "success",
      });
      carregarDados();
    } catch (error: any) {
      try {
        getErrorsByApi(error);
      } catch (e: any) {
        openNotification({ message: e.message, type: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoverSelecionadasN1 = async () => {
    if (selectedN1.length === 0) return;
    if (!window.confirm(`Deseja realmente remover ${selectedN1.length} liberação(ões) de Cadastro N1?`)) return;

    setIsLoading(true);
    try {
      await Promise.all(selectedN1.map((id) => api.delete(`/LiberacaoMOBI/cadastroN1/${id}`)));
      openNotification({
        message: `${selectedN1.length} liberação(ões) N1 removida(s) com sucesso`,
        type: "success",
      });
      setSelectedN1([]);
      carregarDados();
    } catch (error: any) {
      try { getErrorsByApi(error); } catch (e: any) { openNotification({ message: e.message, type: "error" }); }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoverSelecionadasN2 = async () => {
    if (selectedN2.length === 0) return;
    if (!window.confirm(`Deseja realmente remover ${selectedN2.length} liberação(ões) de Cadastro N2?`)) return;

    setIsLoading(true);
    try {
      await Promise.all(selectedN2.map((id) => api.delete(`/LiberacaoMOBI/cadastroN2/${id}`)));
      openNotification({
        message: `${selectedN2.length} liberação(ões) N2 removida(s) com sucesso`,
        type: "success",
      });
      setSelectedN2([]);
      carregarDados();
    } catch (error: any) {
      try { getErrorsByApi(error); } catch (e: any) { openNotification({ message: e.message, type: "error" }); }
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced filter handlers N1
  const debouncedFilterN1 = useCallback(
    debounce((key: keyof typeof filtersN1, value: string) => {
      setFiltersN1((prev) => ({ ...prev, [key]: value }));
    }, 300),
    []
  );

  // Debounced filter handlers N2
  const debouncedFilterN2 = useCallback(
    debounce((key: keyof typeof filtersN2, value: string) => {
      setFiltersN2((prev) => ({ ...prev, [key]: value }));
    }, 300),
    []
  );

  // Filtrar liberações N1
  const filteredLiberacoesN1 = React.useMemo(() => {
    return liberacoesN1.filter((liberacao) => {
      const tipoPacote = (liberacao.tipoPacote || "").toLowerCase();
      const referencia = (liberacao.referencia || "").toLowerCase();
      const linha = (liberacao.linha || "").toLowerCase();
      const data = liberacao.data
        ? new Date(liberacao.data).toLocaleDateString("pt-BR").toLowerCase()
        : "";
      const situacao = (liberacao.situacao || "").toLowerCase();

      const filtroTipoPacote = filtersN1.tipoPacote.trim().toLowerCase();
      const filtroReferencia = filtersN1.referencia.trim().toLowerCase();
      const filtroLinha = filtersN1.linha.trim().toLowerCase();
      const filtroData = filtersN1.data.trim().toLowerCase();
      const filtroSituacao = filtersN1.situacao.trim().toLowerCase();

      const matchTipoPacote = !filtroTipoPacote || tipoPacote.includes(filtroTipoPacote);
      const matchReferencia = !filtroReferencia || referencia.includes(filtroReferencia);
      const matchLinha = !filtroLinha || linha.includes(filtroLinha);
      const matchData = !filtroData || data.includes(filtroData);
      const matchSituacao = !filtroSituacao || situacao.includes(filtroSituacao);

      return matchTipoPacote && matchReferencia && matchLinha && matchData && matchSituacao;
    });
  }, [liberacoesN1, filtersN1]);

  // Filtrar liberações N2
  const filteredLiberacoesN2 = React.useMemo(() => {
    return liberacoesN2.filter((liberacao) => {
      const projetista = (liberacao.projetista || "").toLowerCase();
      const linhaSistema = (liberacao.linhaSistema || "").toLowerCase();
      const data = liberacao.data
        ? new Date(liberacao.data).toLocaleDateString("pt-BR").toLowerCase()
        : "";
      const situacao = (liberacao.situacao || "").toLowerCase();

      const filtroProjetista = filtersN2.projetista.trim().toLowerCase();
      const filtroLinhaSistema = filtersN2.linhaSistema.trim().toLowerCase();
      const filtroData = filtersN2.data.trim().toLowerCase();
      const filtroSituacao = filtersN2.situacao.trim().toLowerCase();

      const matchProjetista = !filtroProjetista || projetista.includes(filtroProjetista);
      const matchLinhaSistema = !filtroLinhaSistema || linhaSistema.includes(filtroLinhaSistema);
      const matchData = !filtroData || data.includes(filtroData);
      const matchSituacao = !filtroSituacao || situacao.includes(filtroSituacao);

      return matchProjetista && matchLinhaSistema && matchData && matchSituacao;
    });
  }, [liberacoesN2, filtersN2]);

  // Colunas CADASTRO N1
  const columnsN1: GridColDef[] = [
    {
      field: "tipoPacote",
      headerName: "Tipo de Pacote",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Tipo de Pacote"
          name="tipoPacote"
          onChange={(event) => debouncedFilterN1("tipoPacote", event.target.value)}
        />
      ),
    },
    {
      field: "referencia",
      headerName: "Referência",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Referência"
          name="referencia"
          onChange={(event) => debouncedFilterN1("referencia", event.target.value)}
        />
      ),
    },
    {
      field: "linha",
      headerName: "Linha",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Linha"
          name="linha"
          onChange={(event) => debouncedFilterN1("linha", event.target.value)}
        />
      ),
    },
    {
      field: "data",
      headerName: "Data",
      flex: 1,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn
          label="Data"
          name="data"
          onChange={(event) => debouncedFilterN1("data", event.target.value)}
        />
      ),
    },
    {
      field: "situacao",
      headerName: "Situação",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Situação"
          name="situacao"
          onChange={(event) => debouncedFilterN1("situacao", event.target.value)}
        />
      ),
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            variant="text"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleRemoverN1(params.row.id)}
            sx={{ minWidth: "auto", px: 1 }}
          >
            Remover
          </Button>
        </Box>
      ),
    },
  ];

  // Colunas CADASTRO N2
  const columnsN2: GridColDef[] = [
    {
      field: "projetista",
      headerName: "Projetista",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Projetista"
          name="projetista"
          onChange={(event) => debouncedFilterN2("projetista", event.target.value)}
        />
      ),
    },
    {
      field: "linhaSistema",
      headerName: "Linha/Sistema",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Linha/Sistema"
          name="linhaSistema"
          onChange={(event) => debouncedFilterN2("linhaSistema", event.target.value)}
        />
      ),
    },
    {
      field: "data",
      headerName: "Data",
      flex: 1,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn
          label="Data"
          name="data"
          onChange={(event) => debouncedFilterN2("data", event.target.value)}
        />
      ),
    },
    {
      field: "situacao",
      headerName: "Situação",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Situação"
          name="situacao"
          onChange={(event) => debouncedFilterN2("situacao", event.target.value)}
        />
      ),
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            variant="text"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => handleRemoverN2(params.row.id)}
            sx={{ minWidth: "auto", px: 1 }}
          >
            Remover
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <DashboardStackCustom>
      <BackdropLoading isOpen={isLoading} />

      {/* Header conforme padrão */}
      <NavigationHeader
        title={`LIBERAÇÕES > ${inquilinoNome || "EMPRESA LTDA"}`}
        label="Home > Cadastros"
        hasBackButton={true}
        goBack={() => navigate("/liberacoesMOBI")}
        buttonLabel="ADICIONAR"
        buttonOnClick={() => {
          if (tabValue === 0) {
            setModalN1Open(true);
          } else {
            setModalN2Open(true);
          }
        }}
      />

      <Paper elevation={3}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="tabs de liberações mobi">
            <Tab label="CADASTRO N1" />
            <Tab label="CADASTRO N2" />
          </Tabs>
        </Box>

        {/* Texto descritivo */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", bgcolor: "#f5f5f5" }}>
          <Typography variant="body2" color="textSecondary">
            {tabValue === 0
              ? "Apresenta os recursos de liberação dos cadastros de nível 1 das Referências + Linhas para o cliente em edição."
              : "Apresenta os recursos para liberar as tipologias dos Projetistas + Linhas/sistemas para o cliente em edição."}
          </Typography>
        </Box>

        {/* Tab Panel - CADASTRO N1 */}
        <TabPanel value={tabValue} index={0}>
          {selectedN1.length > 0 && (
            <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2">{selectedN1.length} selecionada(s)</Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleRemoverSelecionadasN1}
              >
                Remover selecionadas
              </Button>
            </Box>
          )}
          <StripedDataGrid
            columns={columnsN1}
            rows={filteredLiberacoesN1}
            getRowId={(row) => row.id}
            loading={isLoading}
            page={0}
            pageSize={10}
            rowCount={filteredLiberacoesN1.length}
            checkboxSelection
            disableSelectionOnClick
            selectionModel={selectedN1}
            onSelectionModelChange={(model: any[]) => setSelectedN1(model.map(Number))}
            disableColumnMenu
          />
        </TabPanel>

        {/* Tab Panel - CADASTRO N2 */}
        <TabPanel value={tabValue} index={1}>
          {selectedN2.length > 0 && (
            <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2">{selectedN2.length} selecionada(s)</Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleRemoverSelecionadasN2}
              >
                Remover selecionadas
              </Button>
            </Box>
          )}
          <StripedDataGrid
            columns={columnsN2}
            rows={filteredLiberacoesN2}
            getRowId={(row) => row.id}
            loading={isLoading}
            page={0}
            pageSize={10}
            rowCount={filteredLiberacoesN2.length}
            checkboxSelection
            disableSelectionOnClick
            selectionModel={selectedN2}
            onSelectionModelChange={(model: any[]) => setSelectedN2(model.map(Number))}
            disableColumnMenu
          />
        </TabPanel>
      </Paper>

      {/* Modais de Adicionar */}
      <ModalAdicionarN1
        open={modalN1Open}
        onClose={() => setModalN1Open(false)}
        inquilinoId={inquilinoId || ""}
        inquilinoNome={inquilinoNome}
        onSuccess={carregarDados}
      />

      <ModalAdicionarN2
        open={modalN2Open}
        onClose={() => setModalN2Open(false)}
        inquilinoId={inquilinoId || ""}
        inquilinoNome={inquilinoNome}
        onSuccess={carregarDados}
      />
    </DashboardStackCustom>
  );
};

